<template>
  <AuthLayout>
    <div>
      <h1 class="text-2xl font-bold text-gray-900">Create your account</h1>
      <p class="text-sm text-gray-400 mt-1">Complete your registration to get started</p>
    </div>

    <!-- Invalid token error -->
    <div v-if="tokenError" class="mt-5 p-4 rounded-lg bg-red-50 border border-red-100">
      <div class="flex items-start gap-2">
        <svg class="w-5 h-5 text-red-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <div>
          <p class="text-sm font-medium text-red-700">Invalid Invitation</p>
          <p class="text-sm text-red-600 mt-0.5">{{ tokenError }}</p>
        </div>
      </div>
      <router-link to="/login" class="inline-flex items-center gap-1.5 mt-3 text-sm text-[var(--color-primary)] hover:underline">
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        Back to Sign In
      </router-link>
    </div>

    <!-- Registration form -->
    <form v-else @submit.prevent="handleSignUp" class="mt-8">
      <div class="space-y-5">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">First Name</label>
            <input v-model="form.first_name" type="text" required placeholder="John" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">Last Name</label>
            <input v-model="form.last_name" type="text" required placeholder="Doe" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Password</label>
          <PasswordInput v-model="form.password" placeholder="Create a password" :required="true" />
          <!-- Strength meter -->
          <div class="mt-3">
            <div class="flex gap-1.5">
              <div v-for="i in 4" :key="i" class="h-1.5 flex-1 rounded-full transition-all duration-300" :class="i <= strengthLevel ? strengthBarColor : 'bg-gray-100'" />
            </div>
            <p class="text-xs mt-1.5" :class="strengthColor">{{ strengthLabel }}</p>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Confirm Password</label>
          <PasswordInput v-model="form.password_confirmation" placeholder="Confirm your password" :required="true" />
          <p v-if="form.password && form.password_confirmation && form.password !== form.password_confirmation" class="text-xs text-red-500 mt-1.5">Passwords do not match</p>
        </div>
      </div>

      <button type="submit" :disabled="loading || !isValid" class="mt-6 w-full h-11 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        {{ loading ? 'Creating account...' : 'Create Account' }}
      </button>

      <p class="text-center text-sm text-gray-400 mt-4">
        Already have an account? <router-link to="/login" class="text-[var(--color-primary)] hover:underline">Sign in</router-link>
      </p>
    </form>
  </AuthLayout>
</template>

<script>
import AuthLayout from './AuthLayout.vue'
import PasswordInput from '../../components/PasswordInput.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'SignUp',
  components: { AuthLayout, PasswordInput },

  data() {
    return {
      form: { first_name: '', last_name: '', password: '', password_confirmation: '' },
      token: null,
      tokenError: null,
      loading: false
    }
  },

  computed: {
    isValid() {
      return this.form.first_name && this.form.last_name && this.form.password && this.form.password === this.form.password_confirmation && this.form.password.length >= 8
    },
    strengthLevel() {
      const p = this.form.password
      if (!p) return 0
      let s = 0
      if (p.length >= 8) s++
      if (/[A-Z]/.test(p)) s++
      if (/[0-9]/.test(p)) s++
      if (/[^A-Za-z0-9]/.test(p)) s++
      return s
    },
    strengthLabel() {
      const labels = ['', 'Weak', 'Fair', 'Good', 'Strong']
      return labels[this.strengthLevel] || ''
    },
    strengthColor() {
      const colors = ['text-gray-400', 'text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-500']
      return colors[this.strengthLevel] || 'text-gray-400'
    },
    strengthBarColor() {
      const colors = ['bg-gray-200', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-400']
      return colors[this.strengthLevel] || 'bg-gray-200'
    }
  },

  async created() {
    this.token = this.$route.params.token
    if (this.token) {
      try {
        await api.post(`/auth/sign-up/check/${this.token}`)
      } catch (err) {
        this.tokenError = err?.message || 'This invitation link is invalid or has expired.'
      }
    }
  },

  methods: {
    async handleSignUp() {
      if (!this.isValid) return
      this.loading = true
      try {
        await api.post(`/auth/sign-up/${this.token}`, {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          password: this.form.password
        })
        notifySuccess('Account created successfully!')
        this.$router.push({ name: 'sign-in' })
      } catch (err) {
        notifyError(err?.message || 'Failed to create account')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
